import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react'

import { type BrandOption } from './useMarketplacePartnerRegisterPageAutocompleteState'

type UseMarketplacePartnerRegisterPageAutocompleteKeyboardNavigationParams = {
  highlightedIndex: number
  onOptionSelect: (option: BrandOption) => void
  options: BrandOption[]
  setHighlightedIndex: (index: number | ((prev: number) => number)) => void
}

type UseMarketplacePartnerRegisterPageAutocompleteKeyboardNavigationReturn = {
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  optionRefs: RefObject<Map<string, HTMLLIElement>>
}

export const useMarketplacePartnerRegisterPageAutocompleteKeyboardNavigation =
  ({
    highlightedIndex,
    onOptionSelect,
    options,
    setHighlightedIndex,
  }: UseMarketplacePartnerRegisterPageAutocompleteKeyboardNavigationParams): UseMarketplacePartnerRegisterPageAutocompleteKeyboardNavigationReturn => {
    const optionRefs = useRef<Map<string, HTMLLIElement>>(new Map())

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
          case 'ArrowDown': {
            event.preventDefault()
            setHighlightedIndex((prev) => {
              const nextIndex = prev < options.length - 1 ? prev + 1 : prev

              return nextIndex
            })
            break
          }
          case 'ArrowUp': {
            event.preventDefault()
            setHighlightedIndex((prev) => {
              const nextIndex = prev > 0 ? prev - 1 : -1

              return nextIndex
            })
            break
          }
          case 'Enter': {
            event.preventDefault()
            if (highlightedIndex >= 0 && highlightedIndex < options.length) {
              onOptionSelect(options[highlightedIndex])
            }
            break
          }
          case 'Escape': {
            event.preventDefault()
            setHighlightedIndex(-1)
            break
          }
          default: {
            break
          }
        }
      },
      [highlightedIndex, onOptionSelect, options, setHighlightedIndex],
    )
    const prevOptionsRef = useRef<BrandOption[]>(options)

    useEffect(() => {
      if (highlightedIndex < 0) {
        return
      }
      // Only scroll if options haven't changed
      if (prevOptionsRef.current !== options) {
        prevOptionsRef.current = options

        return
      }
      const highlightedOption = options[highlightedIndex]
      const optionElement = optionRefs.current.get(highlightedOption.value)
      if (!optionElement) {
        return
      }
      optionElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, [highlightedIndex, options])

    return {
      handleKeyDown,
      optionRefs,
    }
  }
